import React, { useState, useContext, useEffect, useMemo } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
} from "@material-ui/core";

import PersonIcon from "@material-ui/icons/Person";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/Settings";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import AttachFile from "@material-ui/icons/AttachFile";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import AllInclusive from "@material-ui/icons/AllInclusive";
import DeviceHubOutlined from "@material-ui/icons/DeviceHubOutlined";
import ListIcon from "@material-ui/icons/ListAlt";
import { Webhook, ShapeLine } from "@mui/icons-material";

import MainListItems from "./MainListItems";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";

import { useHistory, useLocation } from "react-router-dom";
import ColorModeContext from "./themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";

const backendUrl = getBackendUrl();
const drawerWidth = 72; // Changed from 240 to fixed collapsed width

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth, // Fixed width
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden", // Mantemos hidden no container principal do drawer para usar flexbox
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRight: "none",
    backgroundColor: theme.mode === "light" ? "#f5f5f5" : theme.palette.background.default,
    boxShadow: "1px 0px 5px rgba(0,0,0,0.05)",
  },
  innerScrollWrapper: {
    // Novo wrapper para a lista
    flex: 1,
    overflowY: "auto", // Habilita rolagem vertical
    overflowX: "hidden",
    width: "100%",
    "&::-webkit-scrollbar": {
      width: "4px", // Scrollbar fina
      display: "block", // Mostrar scrollbar para saber que dá pra rolar
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,0.2)",
      borderRadius: "4px",
    },
  },
  content: {
    flex: 1,
    overflow: "auto", // Alterado de hidden para auto para permitir rolagem
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  containerWithScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
    width: "100%", // Full width of drawer
  },
  logo: {
    width: "40px",
    height: "auto",
    margin: "12px auto",
    display: "block",
  },
  userMenuContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
    // borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  avatar2: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    cursor: "pointer",
    borderRadius: "50%",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  userMenu: {
    color: theme.mode === "light" ? "#666" : "#FFF",
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { user, socket } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [profileUrl, setProfileUrl] = useState(null);

  const settings = useSettings();

  useEffect(() => {
    // Check if user is on mobile
    if (document.body.offsetWidth < 600) {
      // Mobile logic if needed
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        setProfileUrl(`${backendUrl}/public/avatar/${ImageUrl}`);
      else setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(
        () => {
          socket.emit("userStatus");
        },
        1000 * 60 * 5,
      );

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
      };
    }
  }, [socket]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const logoSrc =
    colorMode.mode === "light"
      ? colorMode.appLogoFavicon
      : colorMode.appLogoFavicon;

  const location = useLocation();
  const isKanban =
    location.pathname === "/kanban" ||
    location.pathname === "/tagsKanban" ||
    location.pathname.startsWith("/flowbuilder");

  const isTickets = location.pathname.startsWith("/tickets");
  const hideDrawer = isKanban || (isMobile && isTickets);

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      {!hideDrawer && (
        <Drawer
          variant="permanent"
          className={classes.drawerPaper}
          classes={{
            paper: classes.drawerPaper,
          }}
          open={false}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Logo Section */}
            <img src={logoSrc} alt="logo" className={classes.logo} />

            <div className={classes.innerScrollWrapper}>
              <List component="nav">
                <MainListItems collapsed={true} />
              </List>
            </div>
          </div>

          {/* User Profile Section at Bottom */}
          <div className={classes.userMenuContainer}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              variant="dot"
              onClick={handleMenu}
              aria-controls="menu-appbar"
              aria-haspopup="true"
            >
              <Avatar
                alt={user.name}
                className={classes.avatar2}
                src={profileUrl}
              />
            </StyledBadge>
          </div>
        </Drawer>
      )}

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
        userId={user?.id}
      />

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left", // Show menu to the right of the sidebar
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={menuOpen}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            marginLeft: "60px", // Offset to appear next to sidebar
            marginBottom: "10px",
          },
        }}
      >
        <div
          style={{ padding: "10px 20px", fontWeight: "bold", outline: "none" }}
        >
          {user.name}
        </div>
        <Divider />
        {user.profile === "admin" && (
          <>
            <MenuItem
              onClick={() => {
                history.push("/settings");
                handleCloseMenu();
              }}
              className={classes.userMenu}
            >
              <SettingsIcon
                style={{ marginRight: "8px" }}
                className={classes.userMenu}
              />
              {i18n.t("mainDrawer.appBar.user.settings")}
            </MenuItem>

            <MenuItem
              onClick={() => {
                history.push("/automation");
                handleCloseMenu();
              }}
              className={classes.userMenu}
            >
              <EventAvailableIcon
                style={{ marginRight: "8px" }}
                className={classes.userMenu}
              />
              Automação
            </MenuItem>

            <MenuItem
              onClick={() => {
                history.push("/financeiro");
                handleCloseMenu();
              }}
              className={classes.userMenu}
            >
              <LocalAtmIcon
                style={{ marginRight: "8px" }}
                className={classes.userMenu}
              />
              {i18n.t("mainDrawer.listItems.financeiro")}
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleOpenUserModal} className={classes.userMenu}>
          <PersonIcon
            style={{ marginRight: "8px" }}
            className={classes.userMenu}
          />
          {i18n.t("mainDrawer.appBar.user.profile")}
        </MenuItem>
        <MenuItem onClick={handleClickLogout} className={classes.userMenu}>
          <ExitToAppIcon
            style={{ marginRight: "8px" }}
            className={classes.userMenu}
          />
          {i18n.t("mainDrawer.appBar.user.logout")}
        </MenuItem>
      </Menu>

      <main
        className={classes.content}
        style={{
          backgroundColor:
            location.pathname === "/" ||
            location.pathname === "/settings" ||
            location.pathname === "/automation"
              ? theme.palette.type === "light"
                ? "#f5f5f5"
                : theme.palette.background.default
              : "inherit",
        }}
      >
        {children}
      </main>
    </div>
  );
};

export default LoggedInLayout;
